/**
 * Chat (support) — list sessions. The inbox ships in R1; R2 only needs
 * per-customer session history for the 360 view.
 *
 * Backend doesn't support a `console_user_id` filter on this endpoint
 * today — we fetch the full open/closed list and filter client-side.
 * Cheap enough while volume is low; will need a server-side filter
 * once the customer base grows (flag this when churn starts).
 */

import { request, buildQuery, API_BASE_URL } from './client'
import { useAuthStore } from '@/store/auth'

/** WebSocket URL for the global agent event stream (new sessions,
 * visitor messages, typing dots, draft previews). Auth via
 * `?token=<jwt>`. */
export function chatAgentWsUrl(): string {
  const token = useAuthStore.getState().token
  const base = API_BASE_URL.replace(/^http/, 'ws')
  const qs = new URLSearchParams()
  if (token) qs.set('token', token)
  return `${base}/api/chat/ws/agent${qs.toString() ? `?${qs.toString()}` : ''}`
}

export interface ChatSession {
  id: string
  visitor_id?: string | null
  console_user_id?: string | null
  email?: string | null
  name?: string | null
  status: 'open' | 'closed' | 'missed' | 'snoozed'
  source?: 'widget' | 'whisper' | 'discord' | null
  assigned_agent_id?: string | null
  /** Set together with assigned_agent_id by claim / implicit claim. */
  assigned_agent_email?: string | null
  /** From the anonymous lead form. Only on GET /api/chat/sessions/:id. */
  company?: string | null
  user_agent?: string | null
  last_page_url?: string | null
  city?: string | null
  closed_at?: string | null
  qualification?: string | null
  /* Fields below come from the inbox-triage backend
   * (Relay feat/chat-inbox-backend). Absent on older deployments. */
  last_preview?: string | null
  last_direction?: string | null
  waiting_since?: string | null
  snoozed_until?: string | null
  tags?: string[]
  priority?: boolean
  ip?: string | null
  country?: string | null
  /** Hostname of the site the widget was served on (e.g. soltrace.xyz). */
  host?: string | null
  /**
   * Which identity realm the visitor authenticated against. Console
   * users and GetShreds users live in different collections and share
   * nothing but the JWT signing secret, so this is the only reliable
   * discriminator. ABSENT means "console" — every row written before
   * the field existed has no realm and must not be reclassified.
   */
  realm?: 'console' | 'getshreds' | null
  /** Set only when realm === 'getshreds'. `console_user_id` stays null
   *  for those visitors rather than being namespaced. */
  getshreds_user_id?: string | null
  created_at: string
  last_message_at?: string | null
  unread_count?: number | null
}

export interface ListSessionsParams {
  status?: 'open' | 'closed' | 'missed' | 'snoozed'
  assigned_to_me?: boolean
  limit?: number
  /** Filter to a single originating site (chat_sessions.host). */
  host?: string
}

export async function listChatSessions(
  params: ListSessionsParams = {},
): Promise<{ sessions: ChatSession[] }> {
  return request<{ sessions: ChatSession[] }>(
    `/api/chat/sessions${buildQuery({
      status: params.status,
      assigned_to_me: params.assigned_to_me ? 'true' : undefined,
      limit: params.limit ?? 500,
      host: params.host,
    })}`,
  )
}

/** Client-side filter for a specific console user. */
export async function listSessionsForCustomer(
  consoleUserId: string,
): Promise<ChatSession[]> {
  const r = await listChatSessions({ limit: 500 })
  return (r.sessions ?? []).filter((s) => s.console_user_id === consoleUserId)
}

export type ChatDirection = 'visitor' | 'agent' | 'system' | 'note'

export interface ChatAttachment {
  id?: string
  kind?: 'image' | 'pdf' | string
  url?: string
  filename?: string
  /** Sniffed server-side on the triage backend; absent on older ones. */
  mime?: string
  size?: number
  /** Image pixel size, when the server recorded it. */
  width?: number
  height?: number
  /** Relay deleted the file (chat media is kept 30 days). No URL; the
   *  name and size stay so the thread shows what was sent. */
  expired?: boolean
}

export interface ChatMessage {
  id: string
  session_id: string
  /** Backend values: 'visitor' | 'agent' | 'system'. */
  direction: ChatDirection
  body: string
  agent_id?: string | null
  agent_email?: string | null
  created_at: string
  attachments?: ChatAttachment[]
  read_at?: string | null
  /** Agent-only system event (never shown to the visitor). */
  internal?: boolean
  /** Author name / avatar on agent messages and notes (triage backend). */
  display_name?: string | null
  avatar_url?: string | null
}

export async function listChatMessages(
  sessionId: string,
): Promise<{ messages: ChatMessage[] }> {
  return request<{ messages: ChatMessage[] }>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
  )
}

export async function sendChatMessage(
  sessionId: string,
  body: string,
  attachments?: ChatAttachment[],
): Promise<ChatMessage> {
  // Direction is derived server-side from the caller's principal
  // (admin-JWT → 'agent', visitor-JWT → 'visitor'). No need to pass it.
  const payload: Record<string, unknown> = { body }
  if (attachments && attachments.length > 0) payload.attachments = attachments
  return request<ChatMessage>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
    { method: 'POST', body: payload },
  )
}

/** Full session record. Carries company / user_agent / assignee email,
 *  which the inbox aggregator leaves out. */
export async function getChatSession(sessionId: string): Promise<ChatSession> {
  return request<ChatSession>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}`,
  )
}

/** Assign an unassigned, open session to the caller. The server rejects
 *  it ("already claimed or not open") otherwise. */
export async function claimChatSession(sessionId: string): Promise<ChatSession> {
  return request<ChatSession>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/claim`,
    { method: 'POST' },
  )
}

export type ChatCloseOutcome = 'resolved' | 'support' | 'lead' | 'sale' | 'spam'

/** Close a session. `lead` / `sale` also create a CRM deal when the
 *  session is linked to a console user. There is no reopen endpoint. */
export async function closeChatSession(
  sessionId: string,
  payload: { outcome: ChatCloseOutcome; notes?: string; amount_usd?: number },
): Promise<ChatSession> {
  const body: Record<string, unknown> = { outcome: payload.outcome }
  if (payload.notes && payload.notes.trim()) body.notes = payload.notes.trim()
  if (payload.amount_usd != null) body.amount_usd = payload.amount_usd
  return request<ChatSession>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/close`,
    { method: 'POST', body },
  )
}

/**
 * Upload one file (image or PDF, ≤ 10 MB) for an agent message. The
 * returned attachment goes into `sendChatMessage`'s `attachments`.
 *
 * Uses XMLHttpRequest rather than `request()` because fetch has no
 * upload progress. A 401 retries once through `request()`, which owns
 * the silent token refresh.
 */
export function uploadChatAttachment(
  sessionId: string,
  file: File,
  opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<ChatAttachment> {
  const path = `/api/chat/sessions/${encodeURIComponent(sessionId)}/uploads`
  const viaRequest = () => {
    const fd = new FormData()
    fd.append('file', file)
    return request<ChatAttachment>(path, { method: 'POST', body: fd })
  }
  if (typeof XMLHttpRequest === 'undefined') return viaRequest()

  return new Promise<ChatAttachment>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE_URL}${path}`)
    xhr.setRequestHeader('Accept', 'application/json')
    const token = useAuthStore.getState().token
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total)
    }
    xhr.onload = () => {
      if (xhr.status === 401) {
        viaRequest().then(resolve, reject)
        return
      }
      let body: unknown = null
      try {
        body = JSON.parse(xhr.responseText)
      } catch {
        // non-JSON error page
      }
      if (xhr.status >= 200 && xhr.status < 300 && body) {
        opts.onProgress?.(1)
        resolve(body as ChatAttachment)
      } else {
        const msg =
          (body as { error?: string; message?: string } | null)?.error ??
          (body as { message?: string } | null)?.message ??
          `Upload failed (${xhr.status})`
        reject(Object.assign(new Error(msg), { status: xhr.status }))
      }
    }
    xhr.onerror = () => reject(new Error('Network error while uploading'))
    xhr.onabort = () => reject(Object.assign(new Error('Upload cancelled'), { name: 'AbortError' }))
    opts.signal?.addEventListener('abort', () => xhr.abort())
    const fd = new FormData()
    fd.append('file', file)
    xhr.send(fd)
  })
}

/** Hard-delete a chat session + all its messages. Irreversible. */
export async function deleteChatSession(
  sessionId: string,
): Promise<{ ok: boolean; messages_removed: number }> {
  return request<{ ok: boolean; messages_removed: number }>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE' },
  )
}

export type ChatQualification =
  | 'unqualified'
  | 'lead'
  | 'qualified'
  | 'customer'
  | 'spam'

export async function qualifyChatSession(
  sessionId: string,
  qualification: ChatQualification,
): Promise<unknown> {
  return request(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/qualify`,
    { method: 'PUT', body: { qualification } },
  )
}

/* ──── Inbox triage (Relay feat/chat-inbox-backend) ────
 *
 * None of these exist on older admin_service deployments. Callers treat
 * a 404/405 as "not supported here" (see components/inbox/capabilities)
 * and hide the matching controls instead of surfacing an error.
 */

const sessionPath = (id: string, tail: string) =>
  `/api/chat/sessions/${encodeURIComponent(id)}/${tail}`

export async function markChatRead(
  sessionId: string,
): Promise<{ session_id: string; read_at: string; message_ids: string[] }> {
  return request(sessionPath(sessionId, 'read'), { method: 'POST' })
}

export async function addChatNote(sessionId: string, body: string): Promise<ChatMessage> {
  return request<ChatMessage>(sessionPath(sessionId, 'notes'), { method: 'POST', body: { body } })
}

/** Assign to a teammate, or `null` to unassign. */
export async function setChatAssignee(sessionId: string, agentId: string | null): Promise<ChatSession> {
  return request<ChatSession>(sessionPath(sessionId, 'assignee'), {
    method: 'PUT',
    body: { agent_id: agentId },
  })
}

export async function unclaimChatSession(sessionId: string): Promise<{ status: string; session?: ChatSession }> {
  return request(sessionPath(sessionId, 'unclaim'), { method: 'POST' })
}

export async function reopenChatSession(sessionId: string): Promise<ChatSession> {
  return request<ChatSession>(sessionPath(sessionId, 'reopen'), { method: 'POST' })
}

/** `until` must be in the future and within 365 days (server-checked). */
export async function snoozeChatSession(sessionId: string, until: Date): Promise<ChatSession> {
  return request<ChatSession>(sessionPath(sessionId, 'snooze'), {
    method: 'POST',
    body: { until: until.toISOString() },
  })
}

/** Replaces the whole set. Server lower-cases, trims and de-duplicates;
 *  at most 20 tags of 40 chars. */
export async function setChatTags(sessionId: string, tags: string[]): Promise<ChatSession> {
  return request<ChatSession>(sessionPath(sessionId, 'tags'), { method: 'PUT', body: { tags } })
}

export async function setChatPriority(sessionId: string, priority: boolean): Promise<ChatSession> {
  return request<ChatSession>(sessionPath(sessionId, 'priority'), { method: 'PUT', body: { priority } })
}

/* ──── Saved replies ──── */

export interface SavedReply {
  id: string
  title: string
  /** Plain text with {{variable}} placeholders, filled client-side. */
  body: string
  shortcut?: string | null
  /** Null = shared with the team; set = personal to that agent. */
  owner_id?: string | null
  created_by?: string
  created_at?: string
  updated_at?: string
}

export async function listSavedReplies(): Promise<{ replies: SavedReply[] }> {
  return request<{ replies: SavedReply[] }>('/api/chat/saved-replies')
}

export async function createSavedReply(p: {
  title: string
  body: string
  shortcut?: string
  personal?: boolean
}): Promise<SavedReply> {
  return request<SavedReply>('/api/chat/saved-replies', { method: 'POST', body: p })
}

/** A blank `shortcut` clears it. */
export async function updateSavedReply(
  id: string,
  p: { title?: string; body?: string; shortcut?: string },
): Promise<SavedReply> {
  return request<SavedReply>(`/api/chat/saved-replies/${encodeURIComponent(id)}`, { method: 'PUT', body: p })
}

export async function deleteSavedReply(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/chat/saved-replies/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
